import { cn } from '@/lib/utils';

/**
 * Props for the VideoLoader component
 */
interface VideoLoaderProps {
  /** Size variant of the loader */
  size?: 'small' | 'medium' | 'large';
  /** Optional className for custom styling */
  className?: string;
}

/**
 * VideoLoader Component
 *
 * Minimalist animated spinner with rotating arc.
 * Uses LiveLinx design system colors (primary orange).
 *
 * Features:
 * - Clean, modern design with light grey circle and orange arc
 * - Smooth rotation animation
 * - Responsive sizing (small, medium, large)
 *
 * @example
 * ```tsx
 * <VideoLoader size="medium" />
 * ```
 */
export const VideoLoader = ({ size = 'medium', className }: VideoLoaderProps) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-16 h-16',
    large: 'w-24 h-24',
  };

  return (
    <div className={cn('flex items-center justify-center', sizeClasses[size], className)}>
      <svg
        viewBox="0 0 50 50"
        className="h-full w-full animate-spin"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Base circle (light grey) */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="3"
          className="stroke-livelinx-lightgray100"
          opacity="0.25"
        />

        {/* Animated arc (orange) */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          strokeWidth="3"
          className="stroke-primary"
          strokeLinecap="round"
          strokeDasharray="90 150"
        />
      </svg>
    </div>
  );
};
